import { some } from 'lodash';

export async function sendForgotPasswordEmail(email: string): Promise<void> {
  const requestConfig: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 'email': email })
  }
  const url = '/account/forgot-password';
  const response = await fetch(url, requestConfig);
  await ensureSuccessStatusCode(response, url, requestConfig.method || 'GET');
}

export async function sendAccountEmailConfirmationCode(email: string): Promise<ResendSignUpConfirmationResponseDto> {
  const requestConfig: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 'email': email })
  }
  const url = '/pages/account/resend-email-confirmation-code';
  const response = await fetch(url, requestConfig);
  await ensureSuccessStatusCode(response, url, requestConfig.method || 'GET');
  const responseDto = await response.json() as ResendSignUpConfirmationResponseDto;
  return responseDto;
}

export async function sendExternalAccountEmailConfirmationCode(email: string, provider: string): Promise<void> {
  const requestConfig: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 'email': email, 'provider': provider })
  }
  const url = '/pages/account/resend-external-email-confirmation-code';
  const response = await fetch(url, requestConfig);
  await ensureSuccessStatusCode(response, url, requestConfig.method || 'GET');
}

export function enrichReturnUrlByLoginHint(anchorToEnrich: HTMLAnchorElement, loginHint: string): void {
  const origin = new URL(window.location.href).origin;
  const url = new URL(anchorToEnrich.href, origin);
  const returnUrlParam = url.searchParams.get('returnUrl');
  if (returnUrlParam !== null) {
    const returnUrl = new URL(returnUrlParam, origin);
    returnUrl.searchParams.set('login_hint', loginHint);
    const nativeUrl = returnUrl.pathname + returnUrl.search;
    url.searchParams.set('returnUrl', nativeUrl);
  }
  anchorToEnrich.href = url.href;
}

export function checkPasswordRequirements(password: string): boolean {
  function hasRequiredLength(s: string): boolean {
    return s.length >= 8 && s.length <= 20;
  }

  function isLetterCharacter(c: string): boolean {
    return c.length === 1 && c.toLowerCase() !== c.toUpperCase();
  }

  function containsLowerCaseCharacter(s: string): boolean {
    function isLowerCase(c: string) {
      return isLetterCharacter(c) && c.toLowerCase() === c;
    }
    return some(s, isLowerCase);
  }

  function containsUpperCaseCharacter(s: string): boolean {
    function isUpperCase(c: string) {
      return isLetterCharacter(c) && c.toUpperCase() === c;
    }
    return some(s, isUpperCase);
  }

  function containsNumber(s: string): boolean {
    return /\d/.test(s);
  }

  function containsSpecialCharacter(s: string): boolean {
    return /[`~!@#$%^&*()?{}[\]_-]/.test(s);
  }

  return hasRequiredLength(password) &&
    containsLowerCaseCharacter(password) &&
    (containsUpperCaseCharacter(password) ||
      containsNumber(password) ||
      containsSpecialCharacter(password));
}

export interface ResendSignUpConfirmationResponseDto {
  isAccountAlreadyConfirmed: boolean;
}

export class BackendApiError extends Error {
  public readonly httpStatusCode: number;
  public readonly errorCode: BackEndErrorCode | null;

  constructor(httpStatusCode: number, message: string, errorCode?: BackEndErrorCode) {
    super(message);
    this.httpStatusCode = httpStatusCode;
    this.errorCode = errorCode || null;
  }
}

enum BackEndErrorCode {
  UserIsInPendingState = 4003
}

interface ErrorResponseModel {
  errorCode: BackEndErrorCode,
  message: string,
}

async function ensureSuccessStatusCode(
  response: Response,
  requestUrl: string,
  requestMethod: string) {
  if (response.ok) {
    return;
  }
  if (response.status >= 400 && response.status <= 500) {
    const errorResponse = await response.json() as ErrorResponseModel;
    throw new BackendApiError(response.status, errorResponse.message, errorResponse.errorCode);
  }
  throw new Error(
    'Unexpected status code handled for ' + requestMethod + ': ' + requestUrl + ' request. ' +
    'Status code: ' + response.status.toString() + '.'
  );
}
